import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { label, uploadEvidence, when } from "@/lib/trust";

function ApplyForm({ kind, orgId, criteria, onDone }) {
  const [statement, setStatement] = useState("");
  const [links, setLinks] = useState("");
  const [answers, setAnswers] = useState(criteria.items.map(() => ""));
  const [files, setFiles] = useState(null);
  const submit = async () => {
    try {
      const evidence_file_ids = await uploadEvidence(files);
      await api.post("/verification/applications", { kind, org_id: orgId, statement, criteria_responses: answers, evidence_file_ids,
        links: links.split(/\s+/).filter(Boolean) });
      toast.success("Verification application submitted for independent review");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-3" data-testid={`verification-apply-form-${kind}`}>
      {criteria.description && <p className="text-xs text-slate-400">{criteria.description}</p>}
      {criteria.items.map((c, i) => (
        <div key={i}><label className="field-label" htmlFor={`crit-${i}`}>{i + 1}. {c}</label>
          <textarea id={`crit-${i}`} rows={2} className="field" value={answers[i]} onChange={(e) => setAnswers(answers.map((a, j) => (j === i ? e.target.value : a)))} data-testid={`verification-criterion-${i}`} /></div>
      ))}
      <label className="field-label" htmlFor="ver-statement">Statement (at least 50 characters)</label>
      <textarea id="ver-statement" rows={3} className="field" value={statement} onChange={(e) => setStatement(e.target.value)} data-testid="verification-statement-input" />
      <input className="field" placeholder="Evidence links (space separated URLs)" value={links} onChange={(e) => setLinks(e.target.value)} data-testid="verification-links-input" />
      <input type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={(e) => setFiles(e.target.files)} className="text-xs" data-testid="verification-evidence-input" />
      <button className="btn-gold" onClick={submit} data-testid="verification-submit-button">Submit for verification review</button>
    </div>
  );
}

export function VerificationPanel({ kind, orgId }) {
  const qc = useQueryClient();
  const key = ["verification", kind, orgId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get(`/verification/org/${kind}/${orgId}`).then((r) => r.data) });
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const latest = data.items[0];
  const busy = latest && ["submitted", "under_review"].includes(latest.status);
  return (
    <section className="panel space-y-3 p-5" data-testid={`verification-panel-${kind}`}>
      <p className="eyebrow">Verified {kind === "league" ? "League" : "Community"} program</p>
      <p className="text-sm text-slate-400">Verification is separate from approval. It is never automatic: an independent reviewer checks the published criteria. It lasts 12 months and may be randomly spot-checked after 6 months.</p>
      {latest && <p className="text-sm text-slate-300" data-testid="verification-current-status">Latest: <span className="font-mono uppercase text-aether-300">{label(latest.status)}</span>
        {latest.expires_at && ` · expires ${when(latest.expires_at)}`}{latest.public_reason && ` — ${latest.public_reason}`}</p>}
      {!data.criteria.items.length && <p className="text-xs text-slate-500" data-testid="verification-no-criteria">Staff have not published criteria for this program yet.</p>}
      {data.criteria.items.length > 0 && !busy && !open && <button className="btn-outline" onClick={() => setOpen(true)} data-testid="verification-apply-button">{latest?.status === "approved" ? "Apply to renew" : "Apply for verification"}</button>}
      {open && <ApplyForm kind={kind} orgId={orgId} criteria={data.criteria} onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: key }); }} />}
    </section>
  );
}
